import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/lib/api";
import { getApiUrl } from "@/lib/api";
import { useAuth } from "@/components/ProtectedRoute";
import { BookOpen, Search, Pin, MessageCirclePlus, Settings2, Eye, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

export default function Tutorials() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pinned, setPinned] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);
  const [fbOpen, setFbOpen] = useState(false);
  const [fbMsg, setFbMsg] = useState("");
  const [fbBusy, setFbBusy] = useState(false);
  const [unreadIds, setUnreadIds] = useState([]);
  const [showNewOnly, setShowNewOnly] = useState(false);
  const unreadSet = new Set(unreadIds);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (category) params.set("category", category);
      if (sort) params.set("sort", sort);
      const [p, l, c] = await Promise.all([
        axios.get(`${API}/tutorials/pinned`),
        axios.get(`${API}/tutorials?${params.toString()}`),
        axios.get(`${API}/tutorial-categories`),
      ]);
      setPinned(p.data);
      setTutorials(l.data);
      setCategories(c.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, sort]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { fetchTutorialsUnread(); /* eslint-disable-next-line */ }, []);

  const fetchTutorialsUnread = async () => {
    try {
      const res = await axios.get(`${API}/tutorials/notifications/summary`);
      setUnreadIds(res.data?.unreadIds || []);
    } catch (e) { /* silent */ }
  };

  const openTutorial = (id) => {
    if (unreadSet.has(id)) {
      setUnreadIds((prev) => prev.filter((x) => x !== id));
      setTimeout(() => window.dispatchEvent(new Event("unread-refresh")), 400);
    }
    navigate(`/tutorials/${id}`);
  };

  const clearAllTutorialsNew = async () => {
    setUnreadIds([]);
    setShowNewOnly(false);
    try {
      await axios.post(`${API}/tutorials/notifications/read-all`);
      window.dispatchEvent(new Event("unread-refresh"));
    } catch (e) { /* silent */ }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name;

  const submitFeedback = async () => {
    if (!fbMsg.trim()) return;
    setFbBusy(true);
    try {
      await axios.post(`${API}/tutorials/feedback`, { message: fbMsg.trim() });
      toast.success("Thanks! Your question was sent to the team.");
      setFbMsg(""); setFbOpen(false);
    } catch (e) { toast.error("Failed to send. Please try again."); } finally { setFbBusy(false); }
  };

  const Card = ({ t, pinnedStyle }) => (
    <button
      onClick={() => openTutorial(t.id)}
      className={`text-left rounded-xl border p-4 overflow-hidden transition-all hover:-translate-y-0.5 ${pinnedStyle ? "bg-gradient-to-br from-amber-500/10 to-transparent border-amber-400/30" : unreadSet.has(t.id) ? "bg-white/[0.03] border-red-500/40" : "bg-white/[0.03] border-white/10 hover:border-white/25"}`}
      data-testid={`tutorial-card-${t.id}`}
    >
      {t.coverImage && (
        <div className="-mx-4 -mt-4 mb-3 h-28 overflow-hidden border-b border-white/10" data-testid={`tutorial-cover-${t.id}`}>
          <img
            src={t.coverImage}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-white text-base leading-tight">
          {unreadSet.has(t.id) && (
            <span className="mr-1.5 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold align-middle" data-testid={`tutorial-new-badge-${t.id}`}>New!</span>
          )}
          {t.title}
        </h3>
        {pinnedStyle && <Pin className="w-4 h-4 text-amber-400 shrink-0" />}
      </div>
      {t.summary && <p className="text-sm text-white/55 mt-1 line-clamp-2">{t.summary}</p>}
      <div className="flex items-center gap-3 mt-3 text-[11px] text-white/40">
        {catName(t.categoryId) && <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">{catName(t.categoryId)}</span>}
        <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {t.views || 0}</span>
      </div>
    </button>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8" data-testid="tutorials-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/30 to-purple-500/20 border border-white/10 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-cyan-300" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Tutorials &amp; FAQs</h1>
            <p className="text-sm text-white/50">Learn how to get the most out of the site.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setFbOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-100 hover:bg-cyan-500/30" data-testid="ask-question-btn">
            <MessageCirclePlus className="w-4 h-4" /> Ask a question
          </button>
          {user?.isAdmin && (
            <button onClick={() => navigate("/tutorials/admin")} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white/80 hover:bg-white/10" data-testid="manage-tutorials-btn">
              <Settings2 className="w-4 h-4" /> Manage
            </button>
          )}
        </div>
      </div>

      {pinned.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-amber-300 uppercase tracking-wide flex items-center gap-2"><Pin className="w-4 h-4" /> Pinned</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pinned.map((t) => <Card key={t.id} t={t} pinnedStyle />)}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tutorials..." className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white/90 focus:outline-none focus:border-cyan-400/50" data-testid="tutorial-search" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80" data-testid="tutorial-filter-category">
            <option value="" className="bg-[#0A0A0A]">All categories</option>
            {categories.map((c) => <option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.name}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/80" data-testid="tutorial-sort">
            <option value="newest" className="bg-[#0A0A0A]">Newest</option>
            <option value="title" className="bg-[#0A0A0A]">Title (A–Z)</option>
            <option value="pinned" className="bg-[#0A0A0A]">Pinned first</option>
          </select>
          {unreadIds.length > 0 && (
            <>
              <button
                onClick={() => setShowNewOnly((v) => !v)}
                className={`text-sm px-3 py-2 rounded-lg border ${showNewOnly ? "bg-red-500/25 border-red-500/40 text-white" : "bg-red-500/10 border-red-500/20 text-red-200 hover:bg-red-500/20"}`}
                data-testid="tutorial-show-new-only-toggle"
              >
                {showNewOnly ? "Showing new only" : `Show new only (${unreadIds.length})`}
              </button>
              <button
                onClick={clearAllTutorialsNew}
                className="text-sm px-3 py-2 rounded-lg border bg-white/5 border-white/15 text-white/70 hover:bg-white/10"
                data-testid="tutorial-clear-new-btn"
              >
                Mark all read
              </button>
            </>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
        ) : tutorials.length === 0 ? (
          <p className="text-center text-white/40 py-16">No tutorials found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tutorials.filter((t) => !showNewOnly || unreadSet.has(t.id)).map((t) => <Card key={t.id} t={t} />)}
          </div>
        )}
      </div>

      {fbOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setFbOpen(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white">Ask a question / leave feedback</h3>
            <p className="text-xs text-white/50">Can&apos;t find a tutorial for what you need? Send us your question and we&apos;ll help (and maybe add a tutorial for it).</p>
            <textarea value={fbMsg} onChange={(e) => setFbMsg(e.target.value)} rows={4} placeholder="What would you like help with?" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/90 focus:outline-none focus:border-cyan-400/50" data-testid="feedback-message" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setFbOpen(false)} className="text-sm px-3 py-1.5 rounded border border-white/10 text-white/70">Cancel</button>
              <button onClick={submitFeedback} disabled={fbBusy || !fbMsg.trim()} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50" data-testid="feedback-submit">
                {fbBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
